type SolaWindow = Window & typeof globalThis & {
  setAccount?: (key: string, softwareName: string, softwareVersion: string) => void;
  getTokens?: (
    onSuccess: () => void,
    onError?: () => void,
    timeoutMs?: number,
  ) => void;
};

const normalizeText = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim();

export const SOLA_IFIELDS_VERSION = '3.4.2602.2001';
export const SOLA_IFIELDS_SCRIPT_ID = 'sola-ifields-script';
export const SOLA_IFIELDS_SRC = `https://cdn.cardknox.com/ifields/${SOLA_IFIELDS_VERSION}/ifields.min.js`;
export const SOLA_IFIELDS_FRAME_SRC = `https://cdn.cardknox.com/ifields/${SOLA_IFIELDS_VERSION}/ifield.htm`;

let solaIFieldsScriptPromise: Promise<void> | null = null;

const getRuntimeWindow = (): SolaWindow => {
  if (typeof window === 'undefined') {
    throw new Error('Sola iFields is only available in the browser.');
  }
  return window as SolaWindow;
};

export const loadSolaIFieldsScript = async (): Promise<void> => {
  const runtimeWindow = getRuntimeWindow();
  if (typeof runtimeWindow.getTokens === 'function' && typeof runtimeWindow.setAccount === 'function') {
    return;
  }
  if (solaIFieldsScriptPromise) {
    return solaIFieldsScriptPromise;
  }

  solaIFieldsScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SOLA_IFIELDS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Sola iFields.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SOLA_IFIELDS_SCRIPT_ID;
    script.src = SOLA_IFIELDS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Sola iFields.'));
    document.head.appendChild(script);
  });

  return solaIFieldsScriptPromise;
};

export const configureSolaIFields = async (options: {
  publicIfieldsKey: string;
  softwareName?: string;
  softwareVersion?: string;
}): Promise<void> => {
  const runtimeWindow = getRuntimeWindow();
  await loadSolaIFieldsScript();
  if (typeof runtimeWindow.setAccount !== 'function') {
    throw new Error('Sola iFields did not expose setAccount.');
  }
  runtimeWindow.setAccount(
    options.publicIfieldsKey,
    options.softwareName ?? 'Triedge Flow',
    options.softwareVersion ?? '2026.04',
  );
};

export const tokenizeSolaIFields = async (options: {
  cardTokenInput: HTMLInputElement | null;
  cvvTokenInput: HTMLInputElement | null;
  errorLabel: HTMLElement | null;
  timeoutMs?: number;
}): Promise<{ cardToken: string; cvvToken: string }> => {
  const runtimeWindow = getRuntimeWindow();
  await loadSolaIFieldsScript();
  if (typeof runtimeWindow.getTokens !== 'function') {
    throw new Error('Sola iFields is not ready yet. Please retry in a moment.');
  }

  if (options.cardTokenInput) {
    options.cardTokenInput.value = '';
  }
  if (options.cvvTokenInput) {
    options.cvvTokenInput.value = '';
  }

  return new Promise((resolve, reject) => {
    runtimeWindow.getTokens?.(
      () => {
        const cardToken = normalizeText(options.cardTokenInput?.value);
        const cvvToken = normalizeText(options.cvvTokenInput?.value);
        if (!cardToken || !cvvToken) {
          reject(new Error('Sola iFields did not return card tokens.'));
          return;
        }
        resolve({ cardToken, cvvToken });
      },
      () => {
        reject(
          new Error(
            normalizeText(options.errorLabel?.textContent)
            || 'Sola iFields could not tokenize the donation card.',
          ),
        );
      },
      options.timeoutMs ?? 30000,
    );
  });
};
